import React, { useState } from 'react';

import { connect } from 'react-redux';

import { buildEmptyProperty } from './helper';
import InsightsSuggestedAddressModal from './suggested_address_modal';
import InsightsSearchModal from './search_modal';
import Modal from 'components/modal';
import { oneLine } from 'helpers/formatters/address_formats';
import { pick } from 'lodash';
import { post } from 'helpers/api';

import type { Country } from 'bundles/research/index/types';
import type { Role } from 'bundles/backoffice/contacts/types';

export const DEFAULT_CONTACT_ROLE = 'owner_landlord';

// Pre-select the default role (or the only option), matching the add-contact workflow's behaviour.
export function defaultContactRole(roleOptions?: Role[]): Role | null {
  if (!roleOptions?.length) {
    return null;
  }
  return roleOptions.find(option => option.value === DEFAULT_CONTACT_ROLE) ?? roleOptions[0];
}

interface NewPropertyModalProps {
  backofficeContactId?: number;
  contactRoleOptions?: Role[];
  countryList: Country[];
  newPropertyPath: string;
  show: boolean;
  onHide: () => void;
}

export function NewPropertyModal({
  backofficeContactId,
  contactRoleOptions,
  countryList,
  newPropertyPath,
  show,
  onHide,
}: NewPropertyModalProps) {
  const [searchMessage, setSearchMessage] = useState<string>();
  const [suggestedProperties, setSuggestedProperties] = useState([]);
  const [property, setProperty] = useState(buildEmptyProperty(countryList));
  const [contactRole, setContactRole] = useState<Role | null>(defaultContactRole(contactRoleOptions));

  const changeProperty = (change) => {
    const newProperty = { ...property, ...change };
    setProperty(newProperty);
  };

  const getPropertyParams = (property) => {
    const fields = ['address', 'city'];
    if (property.country?.countryStates?.length) {
      fields.push('state');
    }
    if (property.country?.zipLabel) {
      fields.push('zip');
    }
    return pick(property, fields);
  };

  const createProperty = (property) => {
    let params;
    if (property.insights_id) {
      params = pick(property, ['insights_id']);
    } else {
      params = { ...getPropertyParams(property), country_id: property.country.id };
    }
    if (backofficeContactId) {
      params = {
        ...params,
        backoffice_contact_id: String(backofficeContactId),
        backoffice_contact_role: contactRole?.value ?? DEFAULT_CONTACT_ROLE,
      };
    }
    const urlParams = new URLSearchParams(params);
    window.location.href = `${newPropertyPath}?${urlParams.toString()}`;
  };

  const reset = () => {
    setSearchMessage('');
    setSuggestedProperties([]);
    setProperty(buildEmptyProperty(countryList));
  };

  const handlePropertySearch = async () => {

    const body = {
      insights_property: getPropertyParams(property),
      only: ['address', 'city', 'state', 'zip', 'propertyId']
    };
    const insightResponse = await post('/insights_properties/search', body);

    if (insightResponse['total'] === 0) {
      setSearchMessage(`The address ${oneLine(property)} was not found in Insights. Creating new property.`);
      createProperty(property);
    } else {
      setSuggestedProperties(insightResponse['hits']);
    }
  };

  return (
    <Modal show={show} onHide={onHide} onShow={reset}>
      {suggestedProperties?.length
        ? (
          <InsightsSuggestedAddressModal
            property={property}
            suggestedProperties={suggestedProperties}
            onHide={() => setSuggestedProperties([])}
            onSave={createProperty}
          />
        )
        : (
          <InsightsSearchModal
            contactRole={contactRole}
            contactRoleOptions={contactRoleOptions}
            countryList={countryList}
            info={searchMessage}
            property={property}
            onChange={changeProperty}
            onContactRoleChange={setContactRole}
            onHide={onHide}
            onSearch={handlePropertySearch}
          />
        )}
    </Modal>
  );
}

const mapStateToProps = state => ({
  countryList: state.static.countryList,
});

export default connect(mapStateToProps)(NewPropertyModal);
